import { AnyCommonSynchronizationEnvironmentTypesConstraint } from '../../types/CommonSynchronizationEnvironmentTypes';
import { EntityByEntityName } from '../../types/EntityByEntityName';
import { EntityInfo } from '../EntityInfo/EntityInfo';
import { FieldInfoPath } from '../FieldInfoHandler/FieldInfoHandler';
import { FieldInfoHandlerCache } from '../FieldInfoHandlerCache/FieldInfoHandlerCache';
export class EntityInfoUtils {
  private constructor() {}
  public static getAllRelativeSynchronizationFieldInfos<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntityName extends TCommonSynchronizationEnvironmentTypes['EntityName'], TEntityByEntityName extends EntityByEntityName<TCommonSynchronizationEnvironmentTypes>>(entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, TEntityName, TEntityByEntityName[TEntityName]>): Array<RelativeSynchronizationFieldInfo<TCommonSynchronizationEnvironmentTypes>> {
    const relativeFieldInfos: Array<RelativeSynchronizationFieldInfo<TCommonSynchronizationEnvironmentTypes>> = [];
    const handler = FieldInfoHandlerCache.getForEntityInfo(entityInfo);
    handler.forEachFieldInfo(({
      path,
      fieldInfo
    }) => {
      if (fieldInfo.ref && fieldInfo.relativeSynchronization) {
        relativeFieldInfos.push({
          path,
          ref: fieldInfo.ref
        });
      }
    });
    return relativeFieldInfos;
  }
  public static getAllRefFieldInfos<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint>(entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, any, any>): Array<RefFieldInfo<TCommonSynchronizationEnvironmentTypes>> {
    const refFieldInfos: Array<RefFieldInfo<TCommonSynchronizationEnvironmentTypes>> = [];
    const handler = FieldInfoHandlerCache.getForEntityInfo(entityInfo);
    handler.forEachFieldInfo(({
      path,
      fieldInfo
    }) => {
      if (fieldInfo.ref) {
        refFieldInfos.push({
          path,
          ref: fieldInfo.ref,
          cascadeDeleteLocally: !!fieldInfo.cascadeDeleteLocally,
          relativeSynchronization: !!fieldInfo.relativeSynchronization
        });
      }
    });
    return refFieldInfos;
  }
  public static applyDefaultValues<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntityName extends TCommonSynchronizationEnvironmentTypes['EntityName'], TEntityTypesByEntityName extends EntityByEntityName<TCommonSynchronizationEnvironmentTypes>>(entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, TEntityName, TEntityTypesByEntityName[TEntityName]>, entityData: Record<string | number | symbol, any>): void {
    const handler = FieldInfoHandlerCache.getDefaultValueHandlerForEntityInfo(entityInfo);
    handler.forEachFieldInfoOfData({
      data: entityData,
      callback: ({
        data,
        fieldInfo
      }) => {
        if (data[fieldInfo.name] !== undefined || fieldInfo.defaultValue === undefined) {
          return;
        }
        if (fieldInfo.defaultValue instanceof Function) {
          data[fieldInfo.name] = fieldInfo.defaultValue();
        } else {
          data[fieldInfo.name] = fieldInfo.defaultValue;
        }
      }
    });
  }

  /**
   * @deprecated
   * @param entityInfo
   * @param entity
   */
  public static validateEntityWithLoggingOnly<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntityName extends TCommonSynchronizationEnvironmentTypes['EntityName'], TEntityTypesByEntityName extends EntityByEntityName<TCommonSynchronizationEnvironmentTypes>>(entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, TEntityName, TEntityTypesByEntityName[TEntityName]>, entity: Record<string, any>): asserts entity is TEntityTypesByEntityName[TEntityName] {
    try {
      // let's just assume that external entities are sent correctly to us
      // this is not entirely correct but sadly old data is not stored correctly in the Database yet
      // only validate the entity so we at least can log wrong entities
      EntityInfoUtils.validateEntity(entityInfo, entity);
    } catch (e) {
      // this is just some form of feedback and not logging relevant since it's not a real error
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }
  public static validateEntity<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntityName extends TCommonSynchronizationEnvironmentTypes['EntityName'], TEntityTypesByEntityName extends EntityByEntityName<TCommonSynchronizationEnvironmentTypes>>(entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, TEntityName, TEntityTypesByEntityName[TEntityName]>, entity: Record<string, any>): asserts entity is TEntityTypesByEntityName[TEntityName] {
    if (!entity.id) {
      throw new Error(`entity of type "${entityInfo.entityName}" is missing the property "id"`);
    }
    this.validateEntityWithoutId(entityInfo, entity);
  }
  public static validateEntityWithoutId<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntityName extends TCommonSynchronizationEnvironmentTypes['EntityName'], TEntityTypesByEntityName extends EntityByEntityName<TCommonSynchronizationEnvironmentTypes>>(entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, TEntityName, TEntityTypesByEntityName[TEntityName]>, entity: Record<string, any>): asserts entity is Omit<TEntityTypesByEntityName[TEntityName], 'id'> {
    const handler = FieldInfoHandlerCache.getForEntityInfo(entityInfo);
    handler.forEachFieldInfoOfData({
      data: entity,
      callback: ({
        data,
        fieldInfo,
        path
      }) => {
        if (!Object.prototype.hasOwnProperty.call(data, fieldInfo.name)) {
          throw new Error(`entity of type "${entityInfo.entityName}" is missing the property "${path.join('.')}"`);
        }
      }
    });
  }
}
export type RefFieldInfo<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  path: FieldInfoPath;
  ref: TCommonSynchronizationEnvironmentTypes['EntityName'];
  relativeSynchronization: boolean;
  cascadeDeleteLocally: boolean;
};
export type RelativeSynchronizationFieldInfo<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  path: FieldInfoPath;
  ref: TCommonSynchronizationEnvironmentTypes['EntityName'];
};